/**
 * TokenEvents - Sistema de eventos globales para coordinación de tokens y sockets
 * Permite comunicación entre TokenRefreshManager, SocketService y componentes React
 */

class TokenEvents {
  constructor() {
    this.listeners = new Map();
  }

  /**
   * Subscribe to token-related events
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event).add(callback);

    // Return unsubscribe function
    return () => {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(callback);
        if (eventListeners.size === 0) {
          this.listeners.delete(event);
        }
      }
    };
  }

  /**
   * Unsubscribe from an event
   * @param {string} event - Event name
   * @param {Function} callback - Callback function to remove
   */
  off(event, callback) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
      if (eventListeners.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Emit an event to all subscribers
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          // Silent error to prevent production noise
        }
      });
    }
  }

  /**
   * Get count of listeners for an event
   * @param {string} event - Event name
   * @returns {number} Number of listeners
   */
  listenerCount(event) {
    const eventListeners = this.listeners.get(event);
    return eventListeners ? eventListeners.size : 0;
  }

  /**
   * Clear all listeners for an event or all events
   * @param {string} [event] - Optional event name, if not provided clears all
   */
  clear(event) {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
}

// Singleton instance
const tokenEvents = new TokenEvents();

// Event names constants
export const TOKEN_EVENTS = {
  TOKEN_REFRESHED: 'tokenRefreshed',
  TOKEN_REFRESH_FAILED: 'tokenRefreshFailed',
  SOCKET_RECONNECT_NEEDED: 'socketReconnectNeeded',
  SOCKET_RECONNECTED: 'socketReconnected',
  SOCKET_RECONNECT_FAILED: 'socketReconnectFailed',
  SOCKET_AUTH_ERROR: 'socketAuthError',
};

export default tokenEvents;
